import { useEffect } from "react";
import { apiUrl } from "@/lib/api";
import { getAnonymousId } from "@/lib/tracker";

/* Count a post as shown when it is actually on screen.

   A board renders every post it has, so "rendered" would count the fortieth
   post the same as the first. A post counts once at least half of it has been
   in view, and the ids go to the server in small batches. The server keeps it
   to once per visitor per post per day, so a remount or a scroll back up does
   not count twice.

   Usage: put data-posting-id on each card and pass the list container's ref
   plus something that changes when the list does. */

const FLUSH_MS = 2000;
let pending = new Set();
let timer = null;

function flush() {
  timer = null;
  if (!pending.size) return;
  const ids = [...pending];
  pending = new Set();
  try {
    fetch(apiUrl("/api/accounts/public/postings/impressions/"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids, anonymous_id: getAnonymousId() }),
      credentials: "omit",
      mode: "cors",
      keepalive: true,
    }).catch(() => {});
  } catch { /* a counter, never an error */ }
}

function queue(id) {
  pending.add(id);
  if (!timer) timer = setTimeout(flush, FLUSH_MS);
}

export function usePostingImpressions(containerRef, deps) {
  useEffect(() => {
    const root = containerRef.current;
    if (!root || typeof IntersectionObserver === "undefined") return undefined;
    const seen = new Set();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const id = Number(entry.target.getAttribute("data-posting-id"));
        if (!id || seen.has(id)) return;
        seen.add(id);
        queue(id);
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.5 });
    root.querySelectorAll("[data-posting-id]").forEach((el) => observer.observe(el));
    return () => { observer.disconnect(); flush(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
